import {useCallback, useEffect, useState} from 'react';

import {getAccountPlatformAccess} from 'src/openapi/admin/apis/GetAccountPlatformAccessApi';
import {useAppDispatch} from 'src/store/hooks';

import type {Error as ApiError} from 'src/openapi/admin/models/Error';
import type {UserPlatformAccess} from 'src/openapi/admin/models/UserPlatformAccess';

type Result = {accountId: number, data?: UserPlatformAccess[], error?: ApiError, loading: boolean};

export const useAccountPlatformAccess = (accountId: number, refreshVersion: number) => {
    const dispatch = useAppDispatch();
    const [retryVersion, setRetryVersion] = useState(0);
    const [result, setResult] = useState<Result>({accountId, loading: true});
    const retry = useCallback(() => setRetryVersion(version => version + 1), []);

    useEffect(() => {
        const controller = new AbortController();
        setResult(previous => ({
            accountId, loading: true, data: previous.accountId === accountId ? previous.data : undefined,
        }));
        const load = async () => {
            try {
                const response = await dispatch(getAccountPlatformAccess({accountId}, {signal: controller.signal}));
                if (controller.signal.aborted) {
                    return;
                }
                if (response.payload.success) {
                    setResult({accountId, data: response.payload.data, loading: false});
                } else {
                    const error = response.payload.data;
                    setResult(previous => ({...previous, error, loading: false}));
                }
            } catch {
                if (!controller.signal.aborted) {
                    setResult(previous => ({
                        ...previous, error: {message: 'Failed to load Platform Access'}, loading: false,
                    }));
                }
            }
        };
        load();
        return () => controller.abort();
    }, [accountId, refreshVersion, retryVersion, dispatch]);

    return {...result.accountId === accountId ? result : {loading: true, data: undefined, error: undefined}, retry};
};
